import {
  useAppDispatch,
  useAppSelector
} from '@verzekeringapp-mono/shared/src/hooks/state'
import useClaims from '@verzekeringapp-mono/shared/src/hooks/use_claims'
import {
  getClaimDamageForm,
  getConceptClaim
} from '@verzekeringapp-mono/shared/src/state/selectors/claims'
import { getSelectedPolicy } from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import { setCurrentStep } from '@verzekeringapp-mono/shared/src/state/slices/claims_slice'
import React, { useCallback, useState } from 'react'
import formatPhoneNumber from '@verzekeringapp-mono/shared/src/utils/formatPhoneNumber'
import Button from '~/components/Button'
import formatToApiDate from '@verzekeringapp-mono/shared/src/utils/formatToApiDate'
import CheckboxInput from '~/components/CheckboxInput'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'

const ClaimDamageOverview = () => {
  const selectedPolicy = useAppSelector(getSelectedPolicy)

  const claimDamageForm = useAppSelector(getClaimDamageForm)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const dispatch = useAppDispatch()
  const { storeClaim, createClaimConcept } = useClaims()
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const conceptClaim = useAppSelector(getConceptClaim)
  const [isLoading, setIsLoading] = useState(false)

  const handleCreateConcept = useCallback(async () => {
    if (!selectedPolicy) return

    setIsLoading(true)
    await createClaimConcept({
      policyNumber: selectedPolicy.policyNumber,
      agentNumber: resourceAuthorization?.relationInfo?.agentNumber ?? '',
      relationNumber: Number(resourceAuthorization?.relationInfo?.relationNumber),
      branche: selectedPolicy.branche
    }).then(async claim => {
      if (!claim) {
        setIsLoading(false)

        return
      }

      storeClaim({
        id: claim.id,
        text: claimDamageForm.description || '',
        phoneNumber: claimDamageForm.phoneNumber
          ? formatPhoneNumber(claimDamageForm.phoneNumber)
          : '',
        dateOfLoss: formatToApiDate(claimDamageForm.date) ?? '',
        policyNumber: claimDamageForm.policyId
      }).finally(() => {
        setIsLoading(false)
      })
    })
  }, [claimDamageForm, selectedPolicy, resourceAuthorization])

  const onSendClaim = useCallback(async () => {
    if (conceptClaim && conceptClaim.id) {
      setIsLoading(true)
      await storeClaim({
        id: conceptClaim.id,
        text: claimDamageForm.description || '',
        phoneNumber: claimDamageForm.phoneNumber
          ? formatPhoneNumber(claimDamageForm.phoneNumber)
          : '',
        dateOfLoss: formatToApiDate(claimDamageForm.date) ?? '',
        policyNumber: claimDamageForm.policyId
      }).finally(() => {
        setIsLoading(false)
      })
    } else {
      await handleCreateConcept()
    }
  }, [conceptClaim, claimDamageForm, selectedPolicy, resourceAuthorization])

  return (
    <div className='mx-auto w-full rounded-lg bg-surface-page p-8 lg:w-5/6'>
      <h2
        aria-label='Overzicht schademelding'
        className='mb-4 text-3xl font-medium text-text-primary'
      >Overzicht</h2>

      <p className='mb-6 text-text-secondary'>
        Nog een laatste check, controleer of alle velden nauwkeurig zijn
        ingevuld. Dit helpt je adviseur bij het goed kunnen afwikkelen van de
        schade.
      </p>

      <div className='mb-4'>
        <p className='font-medium text-text-primary'>Polis</p>

        <p className='text-text-primary'>{`${selectedPolicy?.productName} (${claimDamageForm.policyId})`}</p>
      </div>

      <div className='mb-4'>
        <p className='font-medium text-text-primary'>Datum waarop schade is ontstaan</p>

        <p className='text-text-primary'>{claimDamageForm.date}</p>
      </div>

      <div className='mb-4'>
        <p className='font-medium text-text-primary'>
          Geef een gedetailleerde omschrijving van de schade
        </p>

        <p className='wrap-break-word text-text-primary'>{claimDamageForm.description}</p>
      </div>

      <div className='mb-4'>
        <p className='font-medium text-text-primary'>Je telefoonnummer</p>

        {claimDamageForm.phoneNumber && (
          <p className='text-text-primary'>
            {formatPhoneNumber(claimDamageForm.phoneNumber)}
          </p>
        )}
      </div>

      <div className='mb-4'>
        <p className='font-medium text-text-primary'>
          {conceptClaim?.files && conceptClaim?.files.length > 0
            ? 'Bijlage(n)'
            : 'Geen bijlagen'}
        </p>

        {conceptClaim?.files?.map(attachment => (
          <p
            key={attachment.url}
            className='text-text-primary'
          >
            {attachment.title}
          </p>
        ))}
      </div>

      <div className='flex items-center justify-center mb-6'>
        <CheckboxInput
          checked={isConfirmed}
          onChange={e => setIsConfirmed(e.target.checked)}
          label='Ik bevestig dat mijn ingevulde gegevens correct en naar waarheid zijn
          ingevuld. Bij doorzetting door mijn adviseur naar Nh1816 Verzekeringen
          wordt deze schademelding geregistreerd bij stichting CIS.'
        />
      </div>

      <div className='flex justify-between'>
        <Button
          variant='outline'
          onClick={() => {
            dispatch(setCurrentStep(1))
          }}
        >
          <FontAwesomeIcon
            icon={['fal', 'chevron-left']}
            className='mr-2'
          />
          Vorige
        </Button>

        <Button
          onClick={onSendClaim}
          disabled={!isConfirmed}
          isLoading={isLoading}
        >
          <FontAwesomeIcon
            icon={['fal', 'paper-plane']}
            className='mr-2'
          />
          Opsturen
        </Button>
      </div>
    </div>
  )
}

export default ClaimDamageOverview
